import React, { forwardRef, useRef, useState } from 'react';
import Paper from '@mui/material/Paper';

const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, option, [role="button"], [contenteditable="true"]';
const TITLE_SELECTOR = '.MuiDialogTitle-root, [data-dialog-drag-handle="true"]';

const DraggableDialogPaper = forwardRef(function DraggableDialogPaper({ className = '', ...paperProps }, ref) {
  const paperRef = useRef(null);
  const positionRef = useRef({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const isFullScreen = className.split(' ').includes('MuiDialog-paperFullScreen');

  const setPaperRef = (node) => {
    paperRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const handlePointerDown = (event) => {
    const target = event.target;
    const isInteractiveElement = target.closest?.(INTERACTIVE_SELECTOR);
    const isTitle = target.closest?.(TITLE_SELECTOR);
    if (isFullScreen || event.button !== 0 || isInteractiveElement || !isTitle) return;

    const currentPosition = positionRef.current;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: currentPosition.x,
      originY: currentPosition.y,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.currentTarget.style.cursor = 'grabbing';
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current || !paperRef.current) return;
    const bounds = paperRef.current.getBoundingClientRect();
    const maxX = Math.max((window.innerWidth - bounds.width) / 2 - 16, 0);
    const maxY = Math.max((window.innerHeight - bounds.height) / 2 - 16, 0);
    const nextPosition = {
      x: Math.min(Math.max(dragRef.current.originX + event.clientX - dragRef.current.startX, -maxX), maxX),
      y: Math.min(Math.max(dragRef.current.originY + event.clientY - dragRef.current.startY, -maxY), maxY),
    };
    positionRef.current = nextPosition;
    setPosition(nextPosition);
  };

  const handlePointerUp = (event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    event.currentTarget.style.cursor = '';
  };

  return (
    <Paper
      {...paperProps}
      className={className}
      ref={setPaperRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{
        ...paperProps.style,
        left: position.x || undefined,
        top: position.y || undefined,
        position: position.x || position.y ? 'relative' : paperProps.style?.position,
      }}
    />
  );
});

export default DraggableDialogPaper;
